import { Component, OnInit, OnDestroy, OnChanges, Input, SimpleChanges } from "@angular/core";
import { FormGroup, FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ParametroCategoriaDto, OpcionEnum, UpdateParametroCategoriaDto } from "@models/tipoParametro/tipoParametroDto.model";
import { Editor, NgxEditorModule } from "ngx-editor";
import { Observable, of, Subject, map, tap, takeUntil } from "rxjs";
import { ParametroConfiguracionService } from "../../../models/parametroEmpresa/parametro-configuracion.service";
import { TextFieldModule } from "@angular/cdk/text-field";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterModule } from "@angular/router";

interface ParametroAgrupado {
  grupoNombre: string | null;
  iconoGrupo?: string | null;
  parametros: ParametroCategoriaDto[];
}

@Component({
  standalone: true,
  imports: [CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    MatOptionModule,
    TextFieldModule,
    NgxEditorModule,],
  selector: 'app-detalle-categoria',
  templateUrl: './detalle-categoria.component.html',
  styleUrls: ['detalle-categoria.component.scss'],
})
export class DetalleCategoriaComponent implements OnInit, OnDestroy, OnChanges {
  @Input() categoriaRutaSeleccionada: string | null = null;

  configForm: FormGroup;
  parametrosAgrupados$: Observable<ParametroAgrupado[] | null> = of(null);
  isLoading = false;
  isSaving = false;

  private destroy$ = new Subject<void>();
  private initialFormValues: any = {};
  private editorsMap = new Map<string, Editor>(); // Mapa de editores NGX

  constructor(
    private fb: FormBuilder,
    private parametroConfigService: ParametroConfiguracionService,
    private snackBar: MatSnackBar
  ) {
    this.configForm = this.fb.group({});
  }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['categoriaRutaSeleccionada'] &&
      this.categoriaRutaSeleccionada
    ) {
      this.cargarParametros(this.categoriaRutaSeleccionada);
    } else if (
      changes['categoriaRutaSeleccionada'] &&
      !this.categoriaRutaSeleccionada
    ) {
      console.log(
        '[DetalleCategoria] ngOnChanges - categoriaRutaSeleccionada es nula o vacía. Limpiando vista.'
      );
      this.limpiarVista();
    }
  }

  private limpiarVista(): void {
    this.parametrosAgrupados$ = of(null);
    Object.keys(this.configForm.controls).forEach((key) => {
      this.configForm.removeControl(key);
    });
    this.initialFormValues = {};
    this.configForm.markAsPristine();
    // Limpiar editores de manera segura
    this.editorsMap.forEach((editor, key) => {
      try {
        if (editor) {
          editor.destroy();
        }
      } catch (e) {
        console.warn(`Error al destruir editor ${key} en limpiarVista:`, e);
      }
    });
    this.editorsMap.clear();
  }

  private cargarParametros(categoriaRuta: string): void {
    this.isLoading = true;

    this.parametroConfigService
      .getParametrosByCategoria(categoriaRuta)
      .pipe(
        map((parametros) => {
          return this.agruparParametros(parametros);
        }),
        tap(() => {
          this.isLoading = false;
          this.isSaving = false; // Resetear también el estado de guardado
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((agrupados) => {
        this.parametrosAgrupados$ = of(agrupados);
        this.buildForm(agrupados);
      });
  }

  private agruparParametros(
    parametros: ParametroCategoriaDto[]
  ): ParametroAgrupado[] {
    if (!parametros || parametros.length === 0) {
      return [];
    }
    const gruposMap = new Map<
      string | null,
      { icono?: string | null; parametros: ParametroCategoriaDto[] }
    >();
    for (const parametro of parametros) {
      if (!parametro.tipoParametro.visibleEnUI) continue;

      // Parsear opciones usando valorPorDefecto para enum y bool
      if (
        (parametro.tipoParametro.tipoDato === 'enum' ||
          parametro.tipoParametro.tipoDato === 'bool') &&
        parametro.tipoParametro.valorPorDefecto
      ) {
        try {
          const opciones = JSON.parse(parametro.tipoParametro.valorPorDefecto);
          if (Array.isArray(opciones)) {
            (parametro as any)._opcionesParseadas = opciones;
          }
        } catch (e) {
          console.error(
            `Error al parsear valorPorDefecto para ${parametro.tipoParametro.nombre}:`,
            e,
            parametro.tipoParametro.valorPorDefecto
          );
          (parametro as any)._opcionesParseadas = [];
        }
      }

      const grupoKey = parametro.tipoParametro.grupo || 'Otros Parámetros';
      if (!gruposMap.has(grupoKey)) {
        gruposMap.set(grupoKey, {
          parametros: [],
          icono: parametro.tipoParametro.iconoGrupo,
        });
      }
      gruposMap.get(grupoKey)!.parametros.push(parametro);
    }
    const resultadoAgrupacion = Array.from(gruposMap.entries()).map(
      ([grupoNombre, data]) => ({
        grupoNombre,
        iconoGrupo: data.icono,
        parametros: data.parametros.sort(
          (a, b) => (a.tipoParametro.orden ?? 0) - (b.tipoParametro.orden ?? 0)
        ),
      })
    );

    return resultadoAgrupacion;
  }

  private buildForm(grupos: ParametroAgrupado[]): void {
    Object.keys(this.configForm.controls).forEach((key) => {
      this.configForm.removeControl(key);
    });
    this.initialFormValues = {};

    // Limpiar editores anteriores de manera más segura
    this.editorsMap.forEach((editor, key) => {
      try {
        if (editor) {
          editor.destroy();
        }
      } catch (e) {
        console.warn(`Error al destruir editor ${key}:`, e);
      }
    });
    this.editorsMap.clear();

    if (grupos) {
      grupos.forEach((grupo) => {
        grupo.parametros.forEach((param) => {
          let valorInicial = this.obtenerValorInicial(param);

          // Crear validadores
          const validators = this.crearValidadores(param);

          const control = new FormControl(
            {
              value: valorInicial,
              disabled:
                param.tipoParametro.soloLectura ||
                !param.tipoParametro.esEditable,
            },
            validators
          );

          // Crear editor para campos HTML con timeout para evitar conflictos
          if (param.tipoParametro.tipoDato === 'html') {
            setTimeout(() => {
              try {
                const editor = new Editor();
                this.editorsMap.set(param.tipoParametro.nombre, editor);
              } catch (e) {
                console.error(
                  `Error al crear editor para ${param.tipoParametro.nombre}:`,
                  e
                );
              }
            }, 0);
          }

          this.configForm.addControl(param.tipoParametro.nombre, control);
          this.initialFormValues[param.tipoParametro.nombre] = valorInicial;
        });
      });

      // Detectar cambios en el formulario para habilitar/deshabilitar el botón guardar
      this.configForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          // Se maneja automáticamente con formControl.dirty
        });
    } else {
    }
    this.configForm.markAsPristine();
  }

  private obtenerValorInicial(param: ParametroCategoriaDto): any {
    // Usar param.valor si existe y no está vacío, sino buscar el primer valor de las opciones parseadas
    let valorInicial: any = param.valor;

    if (!valorInicial && param.tipoParametro.tipoDato === 'enum') {
      // Si no hay valor inicial y es enum, usar el primer valor de las opciones parseadas
      const opciones = (param as any)._opcionesParseadas;
      if (opciones && opciones.length > 0) {
        valorInicial = opciones[0].valor;
      }
    } else if (!valorInicial && param.tipoParametro.tipoDato === 'bool') {
      // Si no hay valor inicial y es bool, usar false por defecto
      valorInicial = 'false';
    } else if (!valorInicial) {
      // Para otros tipos, usar valorPorDefecto del tipo de parámetro
      valorInicial = param.tipoParametro.valorPorDefecto;
    }

    if (param.tipoParametro.tipoDato === 'bool') {
      // Convertir a boolean para el formulario
      if (typeof valorInicial === 'string') {
        valorInicial = valorInicial === 'true';
      } else if (typeof valorInicial === 'boolean') {
        valorInicial = valorInicial;
      } else {
        valorInicial = false;
      }
    } else if (param.tipoParametro.tipoDato === 'enum' && valorInicial) {
      // Para enums, usar el valor tal como viene (que debería ser el "valor" del objeto enum)
      valorInicial = valorInicial;
    }

    return valorInicial;
  }

  private crearValidadores(param: ParametroCategoriaDto): any[] {
    const validators = [];

    // Validador de requerido
    if (param.tipoParametro.esRequerido && param.tipoParametro.esEditable) {
      validators.push(Validators.required);
    }

    // Validador de patrón/regex
    if (
      param.tipoParametro.validacion &&
      param.tipoParametro.validacion.trim()
    ) {
      try {
        validators.push(Validators.pattern(param.tipoParametro.validacion));
      } catch (e) {
        console.warn(
          `[crearValidadores] ❌ Patrón de validación inválido para ${param.tipoParametro.nombre}:`,
          param.tipoParametro.validacion,
          e
        );
      }
    }

    return validators;
  }

  getOpcionesEnum(parametro: ParametroCategoriaDto): OpcionEnum[] {
    // Si ya tenemos las opciones parseadas, usarlas
    if ((parametro as any)._opcionesParseadas) {
      // console.log(
      //   `[getOpcionesEnum] Retornando opciones parseadas:`,
      //   (parametro as any)._opcionesParseadas
      // );
      return (parametro as any)._opcionesParseadas;
    }

    // Si es tipo bool, usar opciones predeterminadas
    if (parametro.tipoParametro.tipoDato === 'bool') {
      const opcionesDefault = [
        { texto: 'Sí', valor: 'true' },
        { texto: 'No', valor: 'false' },
      ];
      console.log(`[getOpcionesEnum] Es boolean, retornando:`, opcionesDefault);
      return opcionesDefault;
    }

    // Si es enum y tiene valorPorDefecto, intentar parsear manualmente
    if (
      parametro.tipoParametro.tipoDato === 'enum' &&
      parametro.tipoParametro.valorPorDefecto
    ) {
      try {
        console.log(
          `[getOpcionesEnum] Intentando parsear manualmente valorPorDefecto:`,
          parametro.tipoParametro.valorPorDefecto
        );
        const opciones = JSON.parse(parametro.tipoParametro.valorPorDefecto);
        if (Array.isArray(opciones)) {
          console.log(`[getOpcionesEnum] Parseado exitoso:`, opciones);
          // Guardar en cache para próximas llamadas
          (parametro as any)._opcionesParseadas = opciones;
          return opciones;
        } else {
          console.error(
            `[getOpcionesEnum] El resultado del parse no es un array:`,
            opciones
          );
        }
      } catch (e) {
        console.error(
          `[getOpcionesEnum] Error al parsear JSON para ${parametro.tipoParametro.nombre}:`,
          e
        );
        console.error(
          `[getOpcionesEnum] String que falló:`,
          parametro.tipoParametro.valorPorDefecto
        );
      }
    }

    return [];
  }

  // Obtener el editor para un campo HTML específico
  getEditor(nombreParametro: string): Editor | null {
    const editor = this.editorsMap.get(nombreParametro);
    if (editor) {
      return editor;
    }

    // Si no existe el editor, intentar crearlo (para casos donde el timeout aún no se ejecutó)
    try {
      const newEditor = new Editor();
      this.editorsMap.set(nombreParametro, newEditor);
      return newEditor;
    } catch (e) {
      console.warn(`No se pudo crear editor para ${nombreParametro}:`, e);
      return null;
    }
  }

  // Verificar si el formulario ha cambiado y es válido
  get canSave(): boolean {
    return this.configForm.dirty && this.configForm.valid;
  }

  onSubmit(): void {
    if (this.configForm.valid && this.configForm.dirty) {
      this.isSaving = true;
      const formValues = this.configForm.getRawValue();
      const parametrosActualizados: UpdateParametroCategoriaDto[] = [];

      this.parametrosAgrupados$
        .pipe(takeUntil(this.destroy$))
        .subscribe((grupos) => {
          if (grupos) {
            console.log('grupos', grupos);

            grupos.forEach((g) => {
              g.parametros.forEach((p) => {
                const formControlName = p.tipoParametro.nombre;
                if (formValues.hasOwnProperty(formControlName)) {
                  let valorParaGuardar = formValues[formControlName];

                  // Convertir boolean a string para guardar
                  if (p.tipoParametro.tipoDato === 'bool') {
                    console.log('valorParaGuardar', valorParaGuardar);
                    valorParaGuardar = valorParaGuardar ? 'true' : 'false';
                  }

                  // Para otros tipos, convertir a string si no lo es ya
                  if (typeof valorParaGuardar !== 'string') {
                    valorParaGuardar = String(valorParaGuardar || '');
                  }

                  parametrosActualizados.push({
                    id: p.id,
                    descripcion: p.descripcion,
                    observaciones: p.observaciones,
                    valor: valorParaGuardar,
                    idTipoParametro: p.tipoParametro.id,
                  });
                }
              });
            });
          }
        });

      console.log('Parámetros a actualizar:', parametrosActualizados);

      this.parametroConfigService
        .updateParametros(parametrosActualizados)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (success: boolean) => {
            console.log('success', success);

            this.snackBar.open(
              'Configuración guardada correctamente',
              'Cerrar',
              { duration: 3000 }
            );

            // En lugar de recargar todo, solo marcar el formulario como pristine
            // y actualizar los valores iniciales
            this.configForm.markAsPristine();
            const formValues = this.configForm.getRawValue();
            Object.keys(formValues).forEach((key) => {
              this.initialFormValues[key] = formValues[key];
            });

            this.isSaving = false;
          },
          error: (err: any) => {
            console.error('Error al guardar:', err);
            this.snackBar.open('Error al guardar la configuración', 'Cerrar', {
              duration: 3000,
              panelClass: ['error-snackbar'],
            });
            this.isSaving = false;
          },
        });
    } else if (this.configForm.pristine) {
      this.snackBar.open('No hay cambios para guardar', 'Cerrar', {
        duration: 2000,
      });
    } else {
      this.snackBar.open(
        'Por favor, corrige los errores en el formulario',
        'Cerrar',
        { duration: 3000, panelClass: ['error-snackbar'] }
      );
    }
  }

  cancelChanges(): void {
    this.configForm.reset(this.initialFormValues);
    this.configForm.markAsPristine();
    this.snackBar.open('Cambios descartados', 'Cerrar', { duration: 2000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Limpiar editores de manera segura
    this.editorsMap.forEach((editor, key) => {
      try {
        if (editor) {
          editor.destroy();
        }
      } catch (e) {
        console.warn(`Error al destruir editor ${key} en ngOnDestroy:`, e);
      }
    });
    this.editorsMap.clear();
  }

  // Funciones TrackBy
  trackByGrupoNombre(index: number, grupo: ParametroAgrupado): string | null {
    return grupo.grupoNombre;
  }

  trackByParamId(index: number, param: ParametroCategoriaDto): number {
    return param.id;
  }

  trackByOpcionValor(index: number, opcion: OpcionEnum): string {
    return opcion.valor;
  }
}
